import React from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Breadcrumb, Spinner } from '../../../shared/components';
import { formatDateTime } from '../../../shared/utils/localization';
import {
  approveConsultationFieldDefinition,
  createConsultationFieldDefinition,
  getConsultationFieldDefinitionById,
  listConsultationFieldDefinitions,
  rejectConsultationFieldDefinition,
  removeConsultationFieldDefinition,
  updateConsultationFieldDefinition,
} from '../services/consultationService';
import { CATEGORIES_WITH_ITEMS_PRESET_SCHEMA } from '../consultationRepeaterSchema';
import {
  attachmentsSchemaToEditorState,
  CONSULTATION_ATTACHMENT_TYPE_OPTIONS,
  DEFAULT_ATTACHMENTS_SCHEMA_JSON,
  editorStateToAttachmentsSchemaJson,
  parseConsultationAttachmentsSchema,
  type ConsultationAttachmentType,
} from '../consultationAttachmentsSchema';
import {
  DEFAULT_VOICE_SCHEMA_JSON,
  editorStateToVoiceSchemaJson,
  parseConsultationVoiceSchema,
  voiceSchemaToEditorState,
} from '../consultationVoiceSchema';
import type {
  ConsultationFieldDefinition,
  ConsultationFieldOptionInput,
  ConsultationFieldType,
  CreateConsultationFieldDefinitionInput,
} from '../types/Consultation';
import styles from './ConsultationFieldDefinitionsPage.module.css';
import { ConsultationAuthoringBundleMenu } from './ConsultationAuthoringBundleMenu';

type EditorState = {
  name: string;
  fieldType: ConsultationFieldType;
  description: string;
  placeholder: string;
  helpText: string;
  validationJson: string;
  uiSchemaJson: string;
  schemaJson: string;
  code: string;
  isRequired: boolean;
  defaultValue: string;
  options: EditorOptionRow[];
  sortOrder: string;
  isActive: boolean;
};

type EditorOptionRow = {
  key: string;
  id: string;
  label: string;
};

type ValidationRuleKey =
  | 'min'
  | 'max'
  | 'step'
  | 'minLength'
  | 'maxLength'
  | 'regex'
  | 'format'
  | 'minDate'
  | 'maxDate'
  | 'minTime'
  | 'maxTime'
  | 'minDateTime'
  | 'maxDateTime';

type ValidationRulesObject = Partial<Record<ValidationRuleKey, number | string>>;

type UiSchemaKey =
  | 'widget'
  | 'inputMode'
  | 'placeholder'
  | 'helpText'
  | 'rows'
  | 'autocomplete'
  | 'readonly'
  | 'hidden';

type UiSchemaObject = Partial<Record<UiSchemaKey, string | number | boolean>>;

type OptionRowError = {
  id?: string;
  label?: string;
};

type OptionValidationResult = {
  byRowKey: Record<string, OptionRowError>;
  messages: string[];
  firstInvalid?: {
    rowKey: string;
    field: 'id' | 'label';
  };
};

type FieldTypeOption = {
  value: ConsultationFieldType;
  label: string;
};

let optionRowSeed = 0;
const nextOptionRowKey = () => {
  optionRowSeed += 1;
  return `option-row-${optionRowSeed}`;
};

const slugifyOptionId = (value: string): string => {
  const normalized = value
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

  return normalized || 'option';
};

const buildUniqueOptionId = (baseValue: string, usedIds: Set<string>): string => {
  const base = slugifyOptionId(baseValue);
  let candidate = base;
  let index = 2;

  while (usedIds.has(candidate.toLocaleLowerCase())) {
    candidate = `${base}-${index}`;
    index += 1;
  }

  usedIds.add(candidate.toLocaleLowerCase());
  return candidate;
};

const getFieldTypeOptions = (t: ReturnType<typeof useTranslation>['t']): FieldTypeOption[] => [
  { value: 'text', label: t('textFieldType', { defaultValue: 'Text' }) },
  { value: 'longText', label: t('longTextFieldType', { defaultValue: 'Long text' }) },
  { value: 'number', label: t('numberFieldType', { defaultValue: 'Number' }) },
  { value: 'decimal', label: t('decimalFieldType', { defaultValue: 'Decimal' }) },
  { value: 'boolean', label: t('booleanFieldType', { defaultValue: 'Boolean' }) },
  { value: 'date', label: t('dateFieldType', { defaultValue: 'Date' }) },
  { value: 'time', label: t('timeFieldType', { defaultValue: 'Time' }) },
  { value: 'dateTime', label: t('dateTimeFieldType', { defaultValue: 'Date and time' }) },
  { value: 'singleSelect', label: t('singleSelectFieldType', { defaultValue: 'Single select' }) },
  { value: 'multiSelect', label: t('multiSelectFieldType', { defaultValue: 'Multi select' }) },
  { value: 'repeater', label: t('repeaterFieldType', { defaultValue: 'Repeater' }) },
  { value: 'attachments', label: t('attachmentsFieldType', { defaultValue: 'Attachments' }) },
  { value: 'voice', label: t('voiceFieldType', { defaultValue: 'Voice' }) },
];

const emptyEditorState = (): EditorState => ({
  name: '',
  fieldType: 'text',
  description: '',
  placeholder: '',
  helpText: '',
  validationJson: '',
  uiSchemaJson: '',
  schemaJson: '',
  code: '',
  isRequired: false,
  defaultValue: '',
  options: [],
  sortOrder: '0',
  isActive: true,
});

const optionsToEditorText = (options?: ConsultationFieldDefinition['options']): string => {
  if (!options || options.length === 0) {
    return '';
  }

  return options.map((option) => option.label).join(', ');
};

const optionsToEditorRows = (options?: ConsultationFieldDefinition['options']): EditorOptionRow[] => {
  if (!options || options.length === 0) {
    return [];
  }

  return options.map((option) => ({
    key: nextOptionRowKey(),
    id: option.id,
    label: option.label,
  }));
};

const hasAnyOptionData = (options: EditorOptionRow[]): boolean => (
  options.some((option) => option.id.trim() !== '' || option.label.trim() !== '')
);

const normalizeEditorOptions = (options: EditorOptionRow[]): ConsultationFieldOptionInput[] | undefined => {
  const normalized = options
    .map((option) => ({
      id: option.id.trim(),
      label: option.label.trim(),
    }))
    .filter((option) => option.label.length > 0);

  if (normalized.length === 0) {
    return undefined;
  }

  const usedIds = new Set<string>();
  normalized.forEach((option) => {
    if (option.id.length > 0) {
      usedIds.add(option.id.toLocaleLowerCase());
    }
  });

  return normalized.map((option) => ({
    id: option.id.length > 0 ? option.id : buildUniqueOptionId(option.label, usedIds),
    label: option.label,
  }));
};

const normalizeDefinitionOptionsFromEditor = (options: EditorOptionRow[]): ConsultationFieldDefinition['options'] => {
  const normalized = normalizeEditorOptions(options);
  if (!normalized || normalized.length === 0) {
    return undefined;
  }

  return normalized
    .filter((option): option is { id: string; label: string } => typeof option.id === 'string' && option.id.trim().length > 0)
    .map((option) => ({
      id: option.id,
      label: option.label,
    }));
};

const validateEditorOptions = (options: EditorOptionRow[], t: ReturnType<typeof useTranslation>['t']): OptionValidationResult => {
  const byRowKey: Record<string, OptionRowError> = {};
  const messages: string[] = [];
  let firstInvalid: OptionValidationResult['firstInvalid'];
  const labelRequired = t('optionLabelRequired', { defaultValue: 'Label is required.' });
  const labelUnique = t('optionLabelUnique', { defaultValue: 'Label must be unique.' });
  const idUnique = t('optionIdUnique', { defaultValue: 'ID must be unique.' });
  const labelRequiredSummary = t('optionLabelRequiredSummary', { defaultValue: 'Option label is required when an option row is used.' });
  const labelsUniqueSummary = t('optionLabelsUniqueSummary', { defaultValue: 'Option labels must be unique.' });
  const idsUniqueSummary = t('optionIdsUniqueSummary', { defaultValue: 'Option IDs must be unique.' });

  const normalized = options
    .map((option) => ({
      rowKey: option.key,
      id: option.id.trim(),
      label: option.label.trim(),
    }))
    .filter((option) => option.id.length > 0 || option.label.length > 0);

  normalized.forEach((option) => {
    if (option.label.length > 0) {
      return;
    }

    byRowKey[option.rowKey] = {
      ...byRowKey[option.rowKey],
      label: labelRequired,
    };

    if (!firstInvalid) {
      firstInvalid = { rowKey: option.rowKey, field: 'label' };
    }
  });

  const rowsByLabel = new Map<string, string[]>();
  normalized.forEach((option) => {
    if (!option.label) {
      return;
    }

    const key = option.label.toLocaleLowerCase();
    rowsByLabel.set(key, [...(rowsByLabel.get(key) ?? []), option.rowKey]);
  });

  rowsByLabel.forEach((rowKeys) => {
    if (rowKeys.length < 2) {
      return;
    }

    rowKeys.forEach((rowKey) => {
      byRowKey[rowKey] = {
        ...byRowKey[rowKey],
        label: labelUnique,
      };

      if (!firstInvalid) {
        firstInvalid = { rowKey, field: 'label' };
      }
    });
  });

  const rowsById = new Map<string, string[]>();
  normalized.forEach((option) => {
    if (!option.id) {
      return;
    }

    const key = option.id.toLocaleLowerCase();
    rowsById.set(key, [...(rowsById.get(key) ?? []), option.rowKey]);
  });

  rowsById.forEach((rowKeys) => {
    if (rowKeys.length < 2) {
      return;
    }

    rowKeys.forEach((rowKey) => {
      byRowKey[rowKey] = {
        ...byRowKey[rowKey],
        id: idUnique,
      };

      if (!firstInvalid) {
        firstInvalid = { rowKey, field: 'id' };
      }
    });
  });

  if (normalized.some((option) => byRowKey[option.rowKey]?.label === labelRequired)) {
    messages.push(labelRequiredSummary);
  }

  if (normalized.some((option) => byRowKey[option.rowKey]?.label === labelUnique)) {
    messages.push(labelsUniqueSummary);
  }

  if (normalized.some((option) => byRowKey[option.rowKey]?.id === idUnique)) {
    messages.push(idsUniqueSummary);
  }

  return { byRowKey, messages, firstInvalid };
};

const serializeEditorState = (state: EditorState): string => {
  const normalizedOptions = state.options
    .map((option) => ({
      id: option.id.trim(),
      label: option.label.trim(),
    }))
    .filter((option) => option.id.length > 0 || option.label.length > 0);

  return JSON.stringify({
    ...state,
    name: state.name.trim(),
    description: state.description.trim(),
    placeholder: state.placeholder.trim(),
    helpText: state.helpText.trim(),
    validationJson: state.validationJson.trim(),
    uiSchemaJson: state.uiSchemaJson.trim(),
    schemaJson: state.schemaJson.trim(),
    code: state.code.trim(),
    defaultValue: state.defaultValue.trim(),
    sortOrder: state.sortOrder.trim(),
    options: normalizedOptions,
  });
};

const toEditorState = (definition: ConsultationFieldDefinition): EditorState => ({
  name: definition.name,
  fieldType: definition.fieldType,
  description: definition.description ?? '',
  placeholder: definition.placeholder ?? '',
  helpText: definition.helpText ?? '',
  validationJson: definition.validationJson ?? '',
  uiSchemaJson: definition.uiSchemaJson ?? '',
  schemaJson: definition.schemaJson ?? '',
  code: definition.code ?? '',
  isRequired: definition.isRequired,
  defaultValue: definition.defaultValue ?? '',
  options: optionsToEditorRows(definition.options),
  sortOrder: String(definition.sortOrder ?? 0),
  isActive: definition.isActive,
});

const normalizeOptionalText = (value: string) => {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

const isValidJsonString = (value: string): boolean => {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
};

const parseJsonObject = (value: string): Record<string, unknown> | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return {};
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
      return null;
    }

    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
};

const parseValidationRulesObject = (value: string): ValidationRulesObject | null => {
  const parsed = parseJsonObject(value);
  return parsed ? (parsed as ValidationRulesObject) : null;
};

const parseUiSchemaObject = (value: string): UiSchemaObject | null => {
  const parsed = parseJsonObject(value);
  return parsed ? (parsed as UiSchemaObject) : null;
};

const usesOptions = (fieldType: ConsultationFieldType) => fieldType === 'singleSelect' || fieldType === 'multiSelect';
const usesRepeaterSchemaJson = (fieldType: ConsultationFieldType) => fieldType === 'repeater';
const usesAttachmentsSchemaJson = (fieldType: ConsultationFieldType) => fieldType === 'attachments';
const usesVoiceSchemaJson = (fieldType: ConsultationFieldType) => fieldType === 'voice';
const usesSchemaJson = (fieldType: ConsultationFieldType) =>
  usesRepeaterSchemaJson(fieldType) || usesAttachmentsSchemaJson(fieldType) || usesVoiceSchemaJson(fieldType);

const hasDefinitionOptions = (definition: Pick<ConsultationFieldDefinition, 'options'>): boolean => (
  Array.isArray(definition.options) && definition.options.length > 0
);

type DefinitionStatusFilter = 'all' | 'active' | 'inactive';
type DefinitionSortMode = 'sortOrder' | 'updatedDesc' | 'nameAsc';

const VALID_DEFINITION_STATUS_FILTERS = new Set<DefinitionStatusFilter>(['all', 'active', 'inactive']);
const VALID_DEFINITION_SORT_MODES = new Set<DefinitionSortMode>(['sortOrder', 'updatedDesc', 'nameAsc']);

const matchesDefinitionFilter = (definition: ConsultationFieldDefinition, query: string, statusFilter: DefinitionStatusFilter) => {
  if (statusFilter === 'active' && !definition.isActive) {
    return false;
  }

  if (statusFilter === 'inactive' && definition.isActive) {
    return false;
  }

  if (!query) {
    return true;
  }

  const haystack = [
    definition.name,
    definition.code,
    definition.description,
    definition.placeholder,
    definition.helpText,
    definition.fieldType,
    optionsToEditorText(definition.options),
  ]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase();

  return haystack.includes(query);
};

const sortDefinitions = (items: ConsultationFieldDefinition[], sortMode: DefinitionSortMode) => {
  const sorted = [...items];

  switch (sortMode) {
    case 'nameAsc':
      return sorted.sort((left, right) => left.name.localeCompare(right.name));
    case 'updatedDesc':
      return sorted.sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime());
    case 'sortOrder':
    default:
      return sorted.sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name));
  }
};

const getAiStatusLabel = (definition: ConsultationFieldDefinition, t: ReturnType<typeof useTranslation>['t']) => {
  if (!definition.createdByAi) {
    return null;
  }

  if (definition.aiStatus === 'approved') {
    return t('aiApproved', { defaultValue: 'AI approved' });
  }

  if (definition.aiStatus === 'rejected') {
    return t('aiRejected', { defaultValue: 'AI rejected' });
  }

  return t('aiPendingApproval', { defaultValue: 'AI draft - pending approval' });
};

export const ConsultationFieldDefinitionsPage: React.FC = () => {
  const { t } = useTranslation(['consultations', 'calendar', 'common']);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnTo = React.useMemo(() => {
    const state = location.state as { returnTo?: string } | null;
    return state?.returnTo;
  }, [location.state]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [definitions, setDefinitions] = React.useState<ConsultationFieldDefinition[]>([]);
  const selectedDefinitionIdParam = searchParams.get('fieldId');
  const [selectedDefinitionId, setSelectedDefinitionId] = React.useState<string | null>(selectedDefinitionIdParam);
  const [editorState, setEditorState] = React.useState<EditorState>(() => emptyEditorState());
  const [error, setError] = React.useState('');
  const [statusMessage, setStatusMessage] = React.useState('');
  const [deleteConfirmPending, setDeleteConfirmPending] = React.useState(false);
  const [draggedOptionKey, setDraggedOptionKey] = React.useState<string | null>(null);
  const [dropTargetOptionKey, setDropTargetOptionKey] = React.useState<string | null>(null);
  const [showAdvancedValidationJson, setShowAdvancedValidationJson] = React.useState(false);
  const [showAdvancedUiSchemaJson, setShowAdvancedUiSchemaJson] = React.useState(false);
  const optionInputRefs = React.useRef<Record<string, HTMLInputElement | null>>({});

  const searchTerm = searchParams.get('q') ?? '';
  const statusFilter = VALID_DEFINITION_STATUS_FILTERS.has((searchParams.get('status') as DefinitionStatusFilter) ?? 'all')
    ? ((searchParams.get('status') as DefinitionStatusFilter) ?? 'all')
    : 'all';
  const sortMode = VALID_DEFINITION_SORT_MODES.has((searchParams.get('sort') as DefinitionSortMode) ?? 'sortOrder')
    ? ((searchParams.get('sort') as DefinitionSortMode) ?? 'sortOrder')
    : 'sortOrder';
  const hasActiveFilters = Boolean(searchTerm.trim()) || statusFilter !== 'all' || sortMode !== 'sortOrder';

  const updateQueryParam = React.useCallback((key: string, value?: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') {
      next.delete(key);
    } else {
      next.set(key, value);
    }

    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleClearFilters = React.useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('q');
    next.delete('status');
    next.delete('sort');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleCopyLink = React.useCallback(async () => {
    setError('');
    setStatusMessage('');

    if (!navigator.clipboard?.writeText) {
      setError(t('copyLinkNotSupported', { defaultValue: 'Copy link is not supported in this browser.' }));
      return;
    }

    try {
      await navigator.clipboard.writeText(window.location.href);
      setStatusMessage(t('consultationFieldDefinitionsLinkCopied', { defaultValue: 'Field definitions page link copied.' }));
    } catch {
      setError(t('consultationFieldDefinitionsLinkCopyFailed', { defaultValue: 'Failed to copy field definitions page link.' }));
    }
  }, [t]);

  React.useEffect(() => {
    // Sync selection state when URL param changes (e.g. browser back/forward).
    setSelectedDefinitionId(selectedDefinitionIdParam);
    if (!selectedDefinitionIdParam) {
      setEditorState(emptyEditorState());
      return;
    }

    const matched = definitions.find((d) => d.id === selectedDefinitionIdParam) ?? null;
    setEditorState(matched ? toEditorState(matched) : emptyEditorState());
  }, [definitions, selectedDefinitionIdParam]);

  const hydrateDefinitionForEditor = React.useCallback(async (definition: ConsultationFieldDefinition): Promise<ConsultationFieldDefinition> => {
    if (!usesOptions(definition.fieldType) || hasDefinitionOptions(definition)) {
      return definition;
    }

    const detailed = await getConsultationFieldDefinitionById(definition.id);
    if (!detailed || !hasDefinitionOptions(detailed)) {
      return definition;
    }

    return detailed;
  }, []);

  // nextSelectedId is always required — no stale closure on selectedDefinitionId.
  const loadDefinitions = React.useCallback(async (nextSelectedId: string | null) => {
    setLoading(true);
    setError('');

    try {
      const items = await listConsultationFieldDefinitions();
      let sorted = sortDefinitions(items, 'sortOrder');

      if (nextSelectedId) {
        const selected = sorted.find((item) => item.id === nextSelectedId) ?? null;
        const hydratedSelected = selected ? await hydrateDefinitionForEditor(selected) : null;
        if (hydratedSelected && hydratedSelected !== selected) {
          sorted = sortDefinitions(
            sorted.map((item) => (item.id === hydratedSelected.id ? hydratedSelected : item)),
            'sortOrder',
          );
        }

        setDefinitions(sorted);
        setSelectedDefinitionId(hydratedSelected?.id ?? null);
        setEditorState(hydratedSelected ? toEditorState(hydratedSelected) : emptyEditorState());
      } else {
        setDefinitions(sorted);
        setSelectedDefinitionId(null);
        setEditorState(emptyEditorState());
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('consultationFieldDefinitionsLoadFailed', { defaultValue: 'Failed to load consultation field definitions.' }));
    } finally {
      setLoading(false);
    }
  }, [hydrateDefinitionForEditor, t]);

  const initialSelectedIdRef = React.useRef(selectedDefinitionIdParam);
  React.useEffect(() => {
    void loadDefinitions(initialSelectedIdRef.current);
  }, [loadDefinitions]);

  const selectedDefinition = React.useMemo(
    () => definitions.find((item) => item.id === selectedDefinitionId) ?? null,
    [definitions, selectedDefinitionId],
  );

  // True when editor state differs from the persisted definition (or has content for a new one).
  const isDirty = React.useMemo(() => {
    if (!selectedDefinition) {
      return editorState.name.trim() !== '' ||
        editorState.description.trim() !== '' ||
        editorState.placeholder.trim() !== '' ||
        editorState.helpText.trim() !== '' ||
        editorState.validationJson.trim() !== '' ||
        editorState.uiSchemaJson.trim() !== '' ||
        editorState.schemaJson.trim() !== '' ||
        editorState.code.trim() !== '' ||
        editorState.defaultValue.trim() !== '' ||
        hasAnyOptionData(editorState.options);
    }
    const original = toEditorState(selectedDefinition);
    return serializeEditorState(editorState) !== serializeEditorState(original);
  }, [editorState, selectedDefinition]);

  // True when user changed the type of an *existing* definition.
  const fieldTypeChanged = selectedDefinition !== null && editorState.fieldType !== selectedDefinition.fieldType;

  const fieldTypeOptions = React.useMemo(() => getFieldTypeOptions(t), [t]);
  const optionValidation = React.useMemo(() => validateEditorOptions(editorState.options, t), [editorState.options, t]);
  const attachmentsEditorState = React.useMemo(
    () => attachmentsSchemaToEditorState(editorState.schemaJson),
    [editorState.schemaJson],
  );
  const voiceEditorState = React.useMemo(
    () => voiceSchemaToEditorState(editorState.schemaJson),
    [editorState.schemaJson],
  );

  const updateField = React.useCallback(<K extends keyof EditorState>(key: K, value: EditorState[K]) => {
    setEditorState((current) => ({ ...current, [key]: value }));
  }, []);

  const parsedValidationRules = React.useMemo(
    () => parseValidationRulesObject(editorState.validationJson),
    [editorState.validationJson],
  );
  const validationRulesAreInvalidJson = parsedValidationRules === null;

  const parsedUiSchema = React.useMemo(
    () => parseUiSchemaObject(editorState.uiSchemaJson),
    [editorState.uiSchemaJson],
  );
  const uiSchemaIsInvalidJson = parsedUiSchema === null;

  const readValidationRule = React.useCallback((key: ValidationRuleKey): string => {
    if (!parsedValidationRules) {
      return '';
    }

    const value = parsedValidationRules[key];
    if (value === undefined || value === null) {
      return '';
    }

    return String(value);
  }, [parsedValidationRules]);

  const updateValidationRule = React.useCallback((key: ValidationRuleKey, rawValue: string) => {
    const nextValue = rawValue.trim();
    const baseline = parsedValidationRules && !validationRulesAreInvalidJson
      ? { ...parsedValidationRules }
      : {};

    if (!nextValue) {
      delete baseline[key];
    } else if (key === 'min' || key === 'max' || key === 'step' || key === 'minLength' || key === 'maxLength') {
      const numericValue = Number(nextValue);
      baseline[key] = Number.isFinite(numericValue) ? numericValue : nextValue;
    } else {
      baseline[key] = nextValue;
    }

    const compact = Object.entries(baseline).reduce<ValidationRulesObject>((acc, [entryKey, entryValue]) => {
      if (entryValue === undefined || entryValue === null) {
        return acc;
      }

      if (typeof entryValue === 'string' && entryValue.trim() === '') {
        return acc;
      }

      acc[entryKey as ValidationRuleKey] = entryValue;
      return acc;
    }, {});

    updateField('validationJson', Object.keys(compact).length > 0 ? JSON.stringify(compact) : '');
  }, [parsedValidationRules, updateField, validationRulesAreInvalidJson]);

  const readUiSchemaValue = React.useCallback((key: UiSchemaKey): string => {
    if (!parsedUiSchema) {
      return '';
    }

    const value = parsedUiSchema[key];
    if (value === undefined || value === null) {
      return '';
    }

    return String(value);
  }, [parsedUiSchema]);

  const readUiSchemaBoolean = React.useCallback((key: UiSchemaKey): boolean => {
    if (!parsedUiSchema) {
      return false;
    }

    return parsedUiSchema[key] === true;
  }, [parsedUiSchema]);

  const updateUiSchemaValue = React.useCallback((key: UiSchemaKey, rawValue: string) => {
    const nextValue = rawValue.trim();
    const baseline = parsedUiSchema && !uiSchemaIsInvalidJson
      ? { ...parsedUiSchema }
      : {};

    if (!nextValue) {
      delete baseline[key];
    } else if (key === 'rows') {
      const numericValue = Number(nextValue);
      baseline[key] = Number.isFinite(numericValue) ? numericValue : nextValue;
    } else {
      baseline[key] = nextValue;
    }

    const compact = Object.entries(baseline).reduce<UiSchemaObject>((acc, [entryKey, entryValue]) => {
      if (entryValue === undefined || entryValue === null) {
        return acc;
      }

      if (typeof entryValue === 'string' && entryValue.trim() === '') {
        return acc;
      }

      acc[entryKey as UiSchemaKey] = entryValue;
      return acc;
    }, {});

    updateField('uiSchemaJson', Object.keys(compact).length > 0 ? JSON.stringify(compact) : '');
  }, [parsedUiSchema, uiSchemaIsInvalidJson, updateField]);

  const updateUiSchemaBoolean = React.useCallback((key: UiSchemaKey, checked: boolean) => {
    const baseline = parsedUiSchema && !uiSchemaIsInvalidJson
      ? { ...parsedUiSchema }
      : {};

    if (checked) {
      baseline[key] = true;
    } else {
      delete baseline[key];
    }

    const compact = Object.entries(baseline).reduce<UiSchemaObject>((acc, [entryKey, entryValue]) => {
      if (entryValue === undefined || entryValue === null) {
        return acc;
      }

      if (typeof entryValue === 'string' && entryValue.trim() === '') {
        return acc;
      }

      acc[entryKey as UiSchemaKey] = entryValue;
      return acc;
    }, {});

    updateField('uiSchemaJson', Object.keys(compact).length > 0 ? JSON.stringify(compact) : '');
  }, [parsedUiSchema, uiSchemaIsInvalidJson, updateField]);

  const focusFirstInvalidOption = React.useCallback(() => {
    if (!optionValidation.firstInvalid) {
      return;
    }

    const refKey = `${optionValidation.firstInvalid.rowKey}:${optionValidation.firstInvalid.field}`;
    const input = optionInputRefs.current[refKey];
    if (input) {
      input.focus();
      input.select();
    }
  }, [optionValidation.firstInvalid]);

  const filteredDefinitions = React.useMemo(() => {
    const normalizedQuery = searchTerm.trim().toLocaleLowerCase();
    return sortDefinitions(
      definitions.filter((definition) => matchesDefinitionFilter(definition, normalizedQuery, statusFilter)),
      sortMode,
    );
  }, [definitions, searchTerm, sortMode, statusFilter]);

  const handleSelect = React.useCallback(async (definition: ConsultationFieldDefinition) => {
    if (isDirty) {
      setError(t('consultationFieldDefinitionUnsavedChanges', { defaultValue: 'You have unsaved changes. Save or reset the form before switching.' }));
      return;
    }

    let definitionForEditor = definition;
    try {
      definitionForEditor = await hydrateDefinitionForEditor(definition);
      if (definitionForEditor !== definition) {
        setDefinitions((prev) => sortDefinitions(
          prev.map((item) => (item.id === definitionForEditor.id ? definitionForEditor : item)),
          'sortOrder',
        ));
      }
    } catch {
      definitionForEditor = definition;
    }

    updateQueryParam('fieldId', definition.id);
    setSelectedDefinitionId(definition.id);
    setEditorState(toEditorState(definitionForEditor));
    setDeleteConfirmPending(false);
    setError('');
    setStatusMessage('');
  }, [hydrateDefinitionForEditor, isDirty, t, updateQueryParam]);

  const handleCreateNew = React.useCallback(() => {
    if (isDirty) {
      setError(t('consultationFieldDefinitionUnsavedChanges', { defaultValue: 'You have unsaved changes. Save or reset the form before switching.' }));
      return;
    }
    updateQueryParam('fieldId', undefined);
    setSelectedDefinitionId(null);
    setEditorState(emptyEditorState());
    setDeleteConfirmPending(false);
    setError('');
    setStatusMessage('');
  }, [isDirty, t, updateQueryParam]);

  const handleAddOptionRow = React.useCallback(() => {
    updateField('options', [...editorState.options, { key: nextOptionRowKey(), id: '', label: '' }]);
  }, [editorState.options, updateField]);

  const handleUpdateOptionRow = React.useCallback((rowKey: string, key: 'id' | 'label', value: string) => {
    updateField(
      'options',
      editorState.options.map((option) => (option.key === rowKey ? { ...option, [key]: value } : option)),
    );
  }, [editorState.options, updateField]);

  const handleRemoveOptionRow = React.useCallback((rowKey: string) => {
    updateField('options', editorState.options.filter((option) => option.key !== rowKey));
  }, [editorState.options, updateField]);

  const handleMoveOptionRow = React.useCallback((rowKey: string, direction: -1 | 1) => {
    const currentIndex = editorState.options.findIndex((option) => option.key === rowKey);
    if (currentIndex < 0) {
      return;
    }

    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= editorState.options.length) {
      return;
    }

    const reordered = [...editorState.options];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(nextIndex, 0, moved);
    updateField('options', reordered);
  }, [editorState.options, updateField]);

  const handleOptionDragStart = React.useCallback((rowKey: string) => {
    setDraggedOptionKey(rowKey);
    setDropTargetOptionKey(rowKey);
  }, []);

  const handleOptionDrop = React.useCallback((targetRowKey: string) => {
    if (!draggedOptionKey || draggedOptionKey === targetRowKey) {
      setDraggedOptionKey(null);
      setDropTargetOptionKey(null);
      return;
    }

    const fromIndex = editorState.options.findIndex((option) => option.key === draggedOptionKey);
    const toIndex = editorState.options.findIndex((option) => option.key === targetRowKey);
    if (fromIndex < 0 || toIndex < 0) {
      setDraggedOptionKey(null);
      setDropTargetOptionKey(null);
      return;
    }

    const reordered = [...editorState.options];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    updateField('options', reordered);
    setDraggedOptionKey(null);
    setDropTargetOptionKey(null);
  }, [draggedOptionKey, editorState.options, updateField]);

  const handleOptionDragEnd = React.useCallback(() => {
    setDraggedOptionKey(null);
    setDropTargetOptionKey(null);
  }, []);

  const handleSave = React.useCallback(async () => {
    const trimmedName = editorState.name.trim();
    if (!trimmedName) {
      setError(t('consultationFieldDefinitionNameRequired', { defaultValue: 'Field name is required.' }));
      return;
    }

    if (usesOptions(editorState.fieldType) && !normalizeEditorOptions(editorState.options)) {
      setError(t('consultationFieldDefinitionOptionsRequired', { defaultValue: 'Options are required for select fields.' }));
      return;
    }

    if (usesOptions(editorState.fieldType) && optionValidation.messages.length > 0) {
      setError(t('consultationFieldDefinitionOptionsInvalid', { defaultValue: optionValidation.messages[0] }));
      focusFirstInvalidOption();
      return;
    }

    const parsedSortOrder = Number(editorState.sortOrder);
    if (!Number.isFinite(parsedSortOrder)) {
      setError(t('consultationFieldDefinitionSortOrderInvalid', { defaultValue: 'Sort order must be a valid number.' }));
      return;
    }

    const validationJson = normalizeOptionalText(editorState.validationJson);
    if (validationJson && !isValidJsonString(validationJson)) {
      setError(t('consultationFieldDefinitionValidationJsonInvalid', { defaultValue: 'Validation JSON must be valid JSON.' }));
      return;
    }

    const uiSchemaJson = normalizeOptionalText(editorState.uiSchemaJson);
    if (uiSchemaJson && !isValidJsonString(uiSchemaJson)) {
      setError(t('consultationFieldDefinitionUiSchemaJsonInvalid', { defaultValue: 'UI schema JSON must be valid JSON.' }));
      return;
    }

    const schemaJson = normalizeOptionalText(editorState.schemaJson);
    if (usesSchemaJson(editorState.fieldType)) {
      if (!schemaJson) {
        setError(t('consultationFieldDefinitionSchemaJsonRequired', {
          defaultValue: usesAttachmentsSchemaJson(editorState.fieldType)
            ? 'Attachment settings are required for attachment fields.'
            : usesVoiceSchemaJson(editorState.fieldType)
              ? 'Voice settings are required for voice fields.'
              : 'Schema JSON is required for repeater fields.',
        }));
        return;
      }

      if (!isValidJsonString(schemaJson)) {
        setError(t('consultationFieldDefinitionSchemaJsonInvalid', { defaultValue: 'Schema JSON must be valid JSON.' }));
        return;
      }

      if (usesAttachmentsSchemaJson(editorState.fieldType) && !parseConsultationAttachmentsSchema(schemaJson)) {
        setError(t('consultationFieldDefinitionAttachmentsSchemaInvalid', { defaultValue: 'Attachment settings must include allowed types and valid limits.' }));
        return;
      }

      if (usesVoiceSchemaJson(editorState.fieldType) && !parseConsultationVoiceSchema(schemaJson)) {
        setError(t('consultationFieldDefinitionVoiceSchemaInvalid', { defaultValue: 'Voice settings must include valid duration and file size limits.' }));
        return;
      }
    } else if (schemaJson && !isValidJsonString(schemaJson)) {
      setError(t('consultationFieldDefinitionSchemaJsonInvalid', { defaultValue: 'Schema JSON must be valid JSON.' }));
      return;
    }

    const payload: CreateConsultationFieldDefinitionInput = {
      name: trimmedName,
      fieldType: editorState.fieldType,
      description: normalizeOptionalText(editorState.description),
      placeholder: normalizeOptionalText(editorState.placeholder),
      helpText: normalizeOptionalText(editorState.helpText),
      validationJson,
      uiSchemaJson,
      schemaJson: usesSchemaJson(editorState.fieldType) ? schemaJson : undefined,
      code: normalizeOptionalText(editorState.code),
      isRequired: editorState.isRequired,
      defaultValue: normalizeOptionalText(editorState.defaultValue),
      options: usesOptions(editorState.fieldType) ? normalizeEditorOptions(editorState.options) : undefined,
      sortOrder: parsedSortOrder,
      isActive: editorState.isActive,
    };

    setSaving(true);
    setError('');
    setStatusMessage('');

    try {
      const saved = selectedDefinitionId
        ? await updateConsultationFieldDefinition(selectedDefinitionId, payload)
        : await createConsultationFieldDefinition(payload);

      if (!saved) {
        setError(t('consultationFieldDefinitionSaveFailed', { defaultValue: 'Failed to save field definition.' }));
        return;
      }

      const savedWithEditorOptions = usesOptions(editorState.fieldType) && !hasDefinitionOptions(saved)
        ? {
            ...saved,
            options: normalizeDefinitionOptionsFromEditor(editorState.options),
          }
        : saved;

      // Update local state directly — no round-trip to refetch the full list.
      setDefinitions((prev) => {
        if (selectedDefinitionId) {
          return sortDefinitions(prev.map((d) => (d.id === savedWithEditorOptions.id ? savedWithEditorOptions : d)), 'sortOrder');
        }
        return sortDefinitions([...prev, savedWithEditorOptions], 'sortOrder');
      });
      setSelectedDefinitionId(savedWithEditorOptions.id);
      setEditorState(toEditorState(savedWithEditorOptions));
      updateQueryParam('fieldId', savedWithEditorOptions.id);

      setStatusMessage(selectedDefinitionId
        ? t('consultationFieldDefinitionUpdated', { defaultValue: 'Field definition updated.' })
        : t('consultationFieldDefinitionCreated', { defaultValue: 'Field definition created.' }));
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('consultationFieldDefinitionSaveFailed', { defaultValue: 'Failed to save field definition.' }));
    } finally {
      setSaving(false);
    }
  }, [editorState, focusFirstInvalidOption, optionValidation.messages, selectedDefinitionId, t, updateQueryParam]);

  const handleDeleteRequest = React.useCallback(() => {
    if (!selectedDefinition) return;
    setDeleteConfirmPending(true);
    setError('');
    setStatusMessage('');
  }, [selectedDefinition]);

  const handleDeleteCancel = React.useCallback(() => {
    setDeleteConfirmPending(false);
  }, []);

  const handleDeleteConfirm = React.useCallback(async () => {
    if (!selectedDefinition) return;
    setDeleteConfirmPending(false);
    setSaving(true);
    setError('');
    setStatusMessage('');
    try {
      await removeConsultationFieldDefinition(selectedDefinition.id);
      setDefinitions((prev) => prev.filter((d) => d.id !== selectedDefinition.id));
      setSelectedDefinitionId(null);
      setEditorState(emptyEditorState());
      updateQueryParam('fieldId', undefined);
      setStatusMessage(t('consultationFieldDefinitionDeleted', { defaultValue: 'Field definition deleted.' }));
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : t('consultationFieldDefinitionDeleteFailed', { defaultValue: 'Failed to delete field definition.' }));
    } finally {
      setSaving(false);
    }
  }, [selectedDefinition, t, updateQueryParam]);

  const handleApprove = React.useCallback(async () => {
    if (!selectedDefinition) return;

    setSaving(true);
    setError('');
    setStatusMessage('');
    try {
      const updated = await approveConsultationFieldDefinition(selectedDefinition.id);
      if (!updated) {
        setError(t('consultationFieldDefinitionApproveFailed', { defaultValue: 'Failed to approve field definition.' }));
        return;
      }

      setDefinitions((prev) => sortDefinitions(prev.map((item) => (item.id === updated.id ? updated : item)), 'sortOrder'));
      setEditorState(toEditorState(updated));
      setStatusMessage(t('consultationFieldDefinitionApproved', { defaultValue: 'Field definition approved.' }));
    } catch (approveError) {
      setError(approveError instanceof Error ? approveError.message : t('consultationFieldDefinitionApproveFailed', { defaultValue: 'Failed to approve field definition.' }));
    } finally {
      setSaving(false);
    }
  }, [selectedDefinition, t]);

  const handleReject = React.useCallback(async () => {
    if (!selectedDefinition) return;

    setSaving(true);
    setError('');
    setStatusMessage('');
    try {
      const updated = await rejectConsultationFieldDefinition(selectedDefinition.id);
      if (!updated) {
        setError(t('consultationFieldDefinitionRejectFailed', { defaultValue: 'Failed to reject field definition.' }));
        return;
      }

      setDefinitions((prev) => sortDefinitions(prev.map((item) => (item.id === updated.id ? updated : item)), 'sortOrder'));
      setEditorState(toEditorState(updated));
      setStatusMessage(t('consultationFieldDefinitionRejected', { defaultValue: 'Field definition rejected.' }));
    } catch (rejectError) {
      setError(rejectError instanceof Error ? rejectError.message : t('consultationFieldDefinitionRejectFailed', { defaultValue: 'Failed to reject field definition.' }));
    } finally {
      setSaving(false);
    }
  }, [selectedDefinition, t]);

  return (
    <div className={styles.page}>
      <Breadcrumb
        items={[
          { label: t('consultations', { defaultValue: 'Consultations' }), path: '/consultations' },
          { label: t('consultationFieldDefinitions', { defaultValue: 'Field definitions' }), path: '/consultations/field-definitions' },
        ]}
      />

      <section className={styles.hero}>
        <div>
          <h1 className={styles.title}>{t('consultationFieldDefinitions', { defaultValue: 'Consultation field definitions' })}</h1>
          <p className={styles.subtitle}>
            {t('consultationFieldDefinitionsIntro', {
              defaultValue: 'Create reusable structured fields for consultation templates, then assign them where they belong.',
            })}
          </p>
        </div>
        <div className={styles.heroActions}>
          <Link className="btn btn-secondary" to={returnTo || '/consultations'}>
            {returnTo
              ? t('back', { ns: 'common', defaultValue: 'Back' })
              : t('backToConsultations', { defaultValue: 'Back to consultations' })}
          </Link>
          <Link className="btn btn-secondary" to="/consultations/templates">
            {t('manageTemplates', { defaultValue: 'Manage templates' })}
          </Link>
          <button className="btn btn-secondary" type="button" onClick={() => void handleCopyLink()}>
            {t('copyLink', { ns: 'common', defaultValue: 'Copy link' })}
          </button>
          <ConsultationAuthoringBundleMenu
            scope="fieldDefinitions"
            fieldDefinitions={definitions}
            disabled={saving}
            onCompleted={(message) => {
              setError('');
              setStatusMessage(message);
            }}
            onError={(message) => {
              setStatusMessage('');
              setError(message);
            }}
            onImported={() => void loadDefinitions(selectedDefinitionId)}
          />
          <button className="btn btn-primary" type="button" onClick={handleCreateNew}>
            {t('newFieldDefinition', { defaultValue: 'New field definition' })}
          </button>
        </div>
      </section>

      {(error || statusMessage) ? (
        <section className={styles.messages}>
          {error ? <p className={styles.error}>{error}</p> : null}
          {statusMessage ? <p className={styles.success}>{statusMessage}</p> : null}
        </section>
      ) : null}

      {loading ? (
        <Spinner />
      ) : (
        <section className={styles.layout}>
          <aside className={styles.listPanel}>
            <div className={styles.panelHeader}>
              <h2>{t('existingFieldDefinitions', { defaultValue: 'Existing field definitions' })}</h2>
              <span className={styles.count}>{filteredDefinitions.length}/{definitions.length}</span>
            </div>
            <div className={styles.filters}>
              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('search', { ns: 'common', defaultValue: 'Search' })}</label>
                <input
                  className="text-input"
                  value={searchTerm}
                  onChange={(event) => updateQueryParam('q', event.target.value.trim() ? event.target.value : undefined)}
                  placeholder={t('searchFieldDefinitionsPlaceholder', {
                    defaultValue: 'Search by name, description, type, or options',
                  })}
                />
              </div>

              <div className={styles.filterGrid}>
                <div className={styles.fieldRow}>
                  <label className={styles.label}>{t('status', { defaultValue: 'Status' })}</label>
                  <select className="text-input" value={statusFilter} onChange={(event) => updateQueryParam('status', event.target.value)}>
                    <option value="all">{t('allStatuses', { defaultValue: 'All statuses' })}</option>
                    <option value="active">{t('activeOnly', { defaultValue: 'Active only' })}</option>
                    <option value="inactive">{t('inactiveOnly', { defaultValue: 'Inactive only' })}</option>
                  </select>
                </div>

                <div className={styles.fieldRow}>
                  <label className={styles.label}>{t('sortBy', { defaultValue: 'Sort by' })}</label>
                  <select className="text-input" value={sortMode} onChange={(event) => updateQueryParam('sort', event.target.value)}>
                    <option value="sortOrder">{t('sortByConfiguredOrder', { defaultValue: 'Configured order' })}</option>
                    <option value="updatedDesc">{t('sortByRecentlyUpdated', { defaultValue: 'Recently updated' })}</option>
                    <option value="nameAsc">{t('sortByName', { defaultValue: 'Name (A-Z)' })}</option>
                  </select>
                </div>
              </div>

              {hasActiveFilters ? (
                <div className={styles.footer}>
                  <button className="btn btn-secondary" type="button" onClick={handleClearFilters}>
                    {t('clearFilters', { ns: 'common', defaultValue: 'Clear filters' })}
                  </button>
                </div>
              ) : null}
            </div>

            {definitions.length === 0 ? (
              <div className={styles.empty}>
                {t('consultationFieldDefinitionsEmpty', { defaultValue: 'No consultation field definitions exist yet.' })}
              </div>
            ) : filteredDefinitions.length === 0 ? (
              <div className={styles.empty}>
                {t('consultationFieldDefinitionsNoMatch', { defaultValue: 'No field definitions match the current filters.' })}
              </div>
            ) : (
              <div className={styles.definitionList}>
                {filteredDefinitions.map((definition) => (
                  <button
                    key={definition.id}
                    type="button"
                    className={`${styles.definitionCard} ${definition.id === selectedDefinitionId ? styles.definitionCardActive : ''}`}
                    onClick={() => handleSelect(definition)}
                  >
                    <div className={styles.definitionHeader}>
                      <strong>{definition.name}</strong>
                      <div>
                        <span className={`${styles.badge} ${definition.isActive ? styles.badgeActive : styles.badgeMuted}`}>
                          {definition.isActive
                            ? t('active', { ns: 'common', defaultValue: 'Active' })
                            : t('inactive', { defaultValue: 'Inactive' })}
                        </span>
                        {getAiStatusLabel(definition, t) ? (
                          <span className={`${styles.badge} ${styles.badgeMuted}`}>
                            {getAiStatusLabel(definition, t)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className={styles.definitionMeta}>
                      <span>{fieldTypeOptions.find((item) => item.value === definition.fieldType)?.label ?? definition.fieldType}</span>
                      <span>{t('sortOrderLabel', { defaultValue: 'Sort: {{value}}', value: definition.sortOrder })}</span>
                      <span>{definition.isRequired ? t('required', { defaultValue: 'Required' }) : t('optional', { defaultValue: 'Optional' })}</span>
                    </div>
                    <p className={styles.definitionDescription}>{definition.description || t('noDescription', { defaultValue: 'No description' })}</p>
                    <span className={styles.timestamp}>{t('updated', { ns: 'common', defaultValue: 'Updated' })}: {formatDateTime(definition.updatedAt)}</span>
                  </button>
                ))}
              </div>
            )}
          </aside>

          <section className={styles.editorPanel}>
            <div className={styles.panelHeader}>
              <h2>
                {selectedDefinition
                  ? t('editFieldDefinition', { defaultValue: 'Edit field definition' })
                  : t('createFieldDefinition', { defaultValue: 'Create field definition' })}
              </h2>
              {selectedDefinition ? <span className={styles.count}>{selectedDefinition.id.slice(0, 8)}</span> : null}
            </div>

            <div className={styles.formGrid}>
              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('name', { ns: 'common', defaultValue: 'Name' })}</label>
                <input className="text-input" value={editorState.name} onChange={(event) => updateField('name', event.target.value)} maxLength={200} />
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('fieldType', { defaultValue: 'Field type' })}</label>
                <select
                  className="text-input"
                  value={editorState.fieldType}
                  onChange={(event) => {
                    const nextType = event.target.value as ConsultationFieldType;
                    if (nextType === 'attachments' && editorState.fieldType !== 'attachments') {
                      updateField('schemaJson', DEFAULT_ATTACHMENTS_SCHEMA_JSON);
                    } else if (nextType === 'voice' && editorState.fieldType !== 'voice') {
                      updateField('schemaJson', DEFAULT_VOICE_SCHEMA_JSON);
                    } else if (nextType === 'repeater' && editorState.fieldType !== 'repeater' && !editorState.schemaJson.trim()) {
                      updateField('schemaJson', JSON.stringify(CATEGORIES_WITH_ITEMS_PRESET_SCHEMA, null, 2));
                    }
                    updateField('fieldType', nextType);
                  }}
                >
                  {fieldTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('sortOrder', { defaultValue: 'Sort order' })}</label>
                <input className="text-input" type="number" value={editorState.sortOrder} onChange={(event) => updateField('sortOrder', event.target.value)} />
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('description', { defaultValue: 'Description' })}</label>
                <textarea className="text-input" rows={3} value={editorState.description} onChange={(event) => updateField('description', event.target.value)} maxLength={500} />
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('code', { defaultValue: 'Code' })}</label>
                <input className="text-input" value={editorState.code} onChange={(event) => updateField('code', event.target.value)} maxLength={100} />
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('placeholder', { defaultValue: 'Placeholder' })}</label>
                <input className="text-input" value={editorState.placeholder} onChange={(event) => updateField('placeholder', event.target.value)} maxLength={200} />
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('helpText', { defaultValue: 'Help text' })}</label>
                <textarea className="text-input" rows={2} value={editorState.helpText} onChange={(event) => updateField('helpText', event.target.value)} maxLength={500} />
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('validationRules', { defaultValue: 'Validation rules' })}</label>
                <p className={styles.optionsHint}>
                  {t('consultationFieldDefinitionValidationRulesHint', {
                    defaultValue: 'Use guided fields below. JSON editing is optional under Advanced mode.',
                  })}
                </p>

                {(editorState.fieldType === 'number' || editorState.fieldType === 'decimal') ? (
                  <div className={styles.ruleGrid}>
                    <div className={styles.fieldRow}>
                      <label className={styles.label}>{t('minValue', { defaultValue: 'Minimum value' })}</label>
                      <input className="text-input" type="number" value={readValidationRule('min')} onChange={(event) => updateValidationRule('min', event.target.value)} />
                    </div>
                    <div className={styles.fieldRow}>
                      <label className={styles.label}>{t('maxValue', { defaultValue: 'Maximum value' })}</label>
                      <input className="text-input" type="number" value={readValidationRule('max')} onChange={(event) => updateValidationRule('max', event.target.value)} />
                    </div>
                    <div className={styles.fieldRow}>
                      <label className={styles.label}>{t('step', { defaultValue: 'Step' })}</label>
                      <input className="text-input" type="number" value={readValidationRule('step')} onChange={(event) => updateValidationRule('step', event.target.value)} />
                    </div>
                  </div>
                ) : null}

                {(editorState.fieldType === 'text' || editorState.fieldType === 'longText') ? (
                  <div className={styles.ruleGrid}>
                    <div className={styles.fieldRow}>
                      <label className={styles.label}>{t('minLength', { defaultValue: 'Minimum length' })}</label>
                      <input className="text-input" type="number" value={readValidationRule('minLength')} onChange={(event) => updateValidationRule('minLength', event.target.value)} />
                    </div>
                    <div className={styles.fieldRow}>
                      <label className={styles.label}>{t('maxLength', { defaultValue: 'Maximum length' })}</label>
                      <input className="text-input" type="number" value={readValidationRule('maxLength')} onChange={(event) => updateValidationRule('maxLength', event.target.value)} />
                    </div>
                    <div className={styles.fieldRow}>
                      <label className={styles.label}>{t('format', { defaultValue: 'Format' })}</label>
                      <select className="text-input" value={readValidationRule('format')} onChange={(event) => updateValidationRule('format', event.target.value)}>
                        <option value="">{t('none', { defaultValue: 'None' })}</option>
                        <option value="email">Email</option>
                        <option value="url">URL</option>
                      </select>
                    </div>
                    <div className={styles.fieldRow}>
                      <label className={styles.label}>{t('regexPattern', { defaultValue: 'Regex pattern' })}</label>
                      <input className="text-input" value={readValidationRule('regex')} onChange={(event) => updateValidationRule('regex', event.target.value)} placeholder="^[A-Za-z ]+$" maxLength={500} />
                    </div>
                  </div>
                ) : null}

                {editorState.fieldType === 'date' ? (
                  <div className={styles.ruleGrid}>
                    <div className={styles.fieldRow}>
                      <label className={styles.label}>{t('minDate', { defaultValue: 'Earliest date' })}</label>
                      <input className="text-input" type="date" value={readValidationRule('minDate')} onChange={(event) => updateValidationRule('minDate', event.target.value)} />
                    </div>
                    <div className={styles.fieldRow}>
                      <label className={styles.label}>{t('maxDate', { defaultValue: 'Latest date' })}</label>
                      <input className="text-input" type="date" value={readValidationRule('maxDate')} onChange={(event) => updateValidationRule('maxDate', event.target.value)} />
                    </div>
                  </div>
                ) : null}

                {editorState.fieldType === 'time' ? (
                  <div className={styles.ruleGrid}>
                    <div className={styles.fieldRow}>
                      <label className={styles.label}>{t('minTime', { defaultValue: 'Earliest time' })}</label>
                      <input className="text-input" type="time" value={readValidationRule('minTime')} onChange={(event) => updateValidationRule('minTime', event.target.value)} />
                    </div>
                    <div className={styles.fieldRow}>
                      <label className={styles.label}>{t('maxTime', { defaultValue: 'Latest time' })}</label>
                      <input className="text-input" type="time" value={readValidationRule('maxTime')} onChange={(event) => updateValidationRule('maxTime', event.target.value)} />
                    </div>
                  </div>
                ) : null}

                {editorState.fieldType === 'dateTime' ? (
                  <div className={styles.ruleGrid}>
                    <div className={styles.fieldRow}>
                      <label className={styles.label}>{t('minDateTime', { defaultValue: 'Earliest date-time' })}</label>
                      <input className="text-input" value={readValidationRule('minDateTime')} onChange={(event) => updateValidationRule('minDateTime', event.target.value)} placeholder="2026-05-10T08:00:00Z" />
                    </div>
                    <div className={styles.fieldRow}>
                      <label className={styles.label}>{t('maxDateTime', { defaultValue: 'Latest date-time' })}</label>
                      <input className="text-input" value={readValidationRule('maxDateTime')} onChange={(event) => updateValidationRule('maxDateTime', event.target.value)} placeholder="2026-05-10T18:00:00Z" />
                    </div>
                  </div>
                ) : null}

                <label className={styles.checkboxRow}>
                  <input type="checkbox" checked={showAdvancedValidationJson} onChange={(event) => setShowAdvancedValidationJson(event.target.checked)} />
                  <span>{t('showAdvancedValidationJson', { defaultValue: 'Advanced: edit validation JSON manually' })}</span>
                </label>

                {showAdvancedValidationJson ? (
                  <>
                    <textarea className="text-input" rows={4} value={editorState.validationJson} onChange={(event) => updateField('validationJson', event.target.value)} maxLength={4000} />
                    {validationRulesAreInvalidJson ? (
                      <span className={styles.optionInlineError}>{t('consultationFieldDefinitionValidationJsonInvalidInline', { defaultValue: 'Validation JSON is invalid. Fix JSON syntax or disable advanced edit and use guided fields.' })}</span>
                    ) : null}
                  </>
                ) : null}
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('uiSchemaJson', { defaultValue: 'UI schema JSON' })}</label>
                <p className={styles.optionsHint}>{t('uiSchemaGuidedHint', { defaultValue: 'Use these presentation settings instead of writing JSON manually.' })}</p>
                <div className={styles.ruleGrid}>
                  <div className={styles.fieldRow}>
                    <label className={styles.label}>{t('uiWidget', { defaultValue: 'Widget' })}</label>
                    <select className="text-input" value={readUiSchemaValue('widget')} onChange={(event) => updateUiSchemaValue('widget', event.target.value)}>
                      <option value="">{t('defaultWidget', { defaultValue: 'Default' })}</option>
                      {editorState.fieldType === 'longText' ? <option value="textarea">Textarea</option> : null}
                      {(editorState.fieldType === 'number' || editorState.fieldType === 'decimal') ? <option value="number">Numeric input</option> : null}
                      {(editorState.fieldType === 'date' || editorState.fieldType === 'time' || editorState.fieldType === 'dateTime') ? <option value="native">Native picker</option> : null}
                    </select>
                  </div>

                  {(editorState.fieldType === 'number' || editorState.fieldType === 'decimal') ? (
                    <div className={styles.fieldRow}>
                      <label className={styles.label}>{t('inputMode', { defaultValue: 'Input mode' })}</label>
                      <select className="text-input" value={readUiSchemaValue('inputMode')} onChange={(event) => updateUiSchemaValue('inputMode', event.target.value)}>
                        <option value="">{t('defaultInputMode', { defaultValue: 'Default' })}</option>
                        <option value="numeric">Numeric</option>
                        <option value="decimal">Decimal</option>
                      </select>
                    </div>
                  ) : null}

                  <div className={styles.fieldRow}>
                    <label className={styles.label}>{t('uiPlaceholderOverride', { defaultValue: 'Placeholder override' })}</label>
                    <input className="text-input" value={readUiSchemaValue('placeholder')} onChange={(event) => updateUiSchemaValue('placeholder', event.target.value)} maxLength={250} />
                  </div>

                  <div className={styles.fieldRow}>
                    <label className={styles.label}>{t('uiHelpTextOverride', { defaultValue: 'Help text override' })}</label>
                    <input className="text-input" value={readUiSchemaValue('helpText')} onChange={(event) => updateUiSchemaValue('helpText', event.target.value)} maxLength={500} />
                  </div>

                  <div className={styles.fieldRow}>
                    <label className={styles.label}>{t('autocomplete', { defaultValue: 'Autocomplete' })}</label>
                    <input className="text-input" value={readUiSchemaValue('autocomplete')} onChange={(event) => updateUiSchemaValue('autocomplete', event.target.value)} placeholder="off" maxLength={100} />
                  </div>

                  {editorState.fieldType === 'longText' ? (
                    <div className={styles.fieldRow}>
                      <label className={styles.label}>{t('rows', { defaultValue: 'Rows' })}</label>
                      <input className="text-input" type="number" min={1} value={readUiSchemaValue('rows')} onChange={(event) => updateUiSchemaValue('rows', event.target.value)} />
                    </div>
                  ) : null}

                  <label className={styles.checkboxRow}>
                    <input type="checkbox" checked={readUiSchemaBoolean('readonly')} onChange={(event) => updateUiSchemaBoolean('readonly', event.target.checked)} />
                    <span>{t('readonly', { defaultValue: 'Read-only field' })}</span>
                  </label>

                  <label className={styles.checkboxRow}>
                    <input type="checkbox" checked={readUiSchemaBoolean('hidden')} onChange={(event) => updateUiSchemaBoolean('hidden', event.target.checked)} />
                    <span>{t('hiddenField', { defaultValue: 'Hidden field' })}</span>
                  </label>
                </div>

                <label className={styles.checkboxRow}>
                  <input type="checkbox" checked={showAdvancedUiSchemaJson} onChange={(event) => setShowAdvancedUiSchemaJson(event.target.checked)} />
                  <span>{t('showUiSchemaAdvanced', { defaultValue: 'Advanced: edit UI schema JSON' })}</span>
                </label>
                {showAdvancedUiSchemaJson ? (
                  <>
                    <textarea className="text-input" rows={4} value={editorState.uiSchemaJson} onChange={(event) => updateField('uiSchemaJson', event.target.value)} maxLength={4000} />
                    {uiSchemaIsInvalidJson ? (
                      <span className={styles.optionInlineError}>{t('consultationFieldDefinitionUiSchemaJsonInvalidInline', { defaultValue: 'UI schema JSON is invalid. Fix JSON syntax or disable advanced edit and use guided fields.' })}</span>
                    ) : null}
                  </>
                ) : (
                  <p className={styles.optionsHint}>{t('uiSchemaOptionalHint', { defaultValue: 'UI schema is optional and typically only needed for advanced rendering behavior.' })}</p>
                )}
              </div>

              {usesRepeaterSchemaJson(editorState.fieldType) ? (
                <div className={styles.fieldRow}>
                  <label className={styles.label}>{t('schemaJson', { defaultValue: 'Repeater schema JSON' })}</label>
                  <p className={styles.optionsHint}>
                    {t('schemaJsonHint', {
                      defaultValue: 'Define nested rows and child fields. Use the preset for categories with items (name + date).',
                    })}
                  </p>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => updateField('schemaJson', JSON.stringify(CATEGORIES_WITH_ITEMS_PRESET_SCHEMA, null, 2))}
                  >
                    {t('applyCategoriesPreset', { defaultValue: 'Apply categories with items preset' })}
                  </button>
                  <textarea
                    className="text-input"
                    rows={12}
                    value={editorState.schemaJson}
                    onChange={(event) => updateField('schemaJson', event.target.value)}
                    maxLength={16000}
                  />
                </div>
              ) : null}

              {usesAttachmentsSchemaJson(editorState.fieldType) ? (
                <div className={styles.fieldRow}>
                  <label className={styles.label}>{t('attachmentsSettings', { defaultValue: 'Attachment settings' })}</label>
                  <p className={styles.optionsHint}>
                    {t('attachmentsSettingsHint', {
                      defaultValue: 'Configure drag-and-drop, allowed file types, and upload limits for this field.',
                    })}
                  </p>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={attachmentsEditorState.allowDragDrop}
                      onChange={(event) => updateField(
                        'schemaJson',
                        editorStateToAttachmentsSchemaJson({
                          ...attachmentsEditorState,
                          allowDragDrop: event.target.checked,
                        }),
                      )}
                    />
                    <span>{t('allowDragDrop', { defaultValue: 'Allow drag and drop' })}</span>
                  </label>
                  <div className={styles.fieldRow}>
                    <label className={styles.label}>{t('maxFileSizeMb', { defaultValue: 'Max file size (MB)' })}</label>
                    <input
                      className="text-input"
                      type="number"
                      min={1}
                      value={attachmentsEditorState.maxFileSizeMb}
                      onChange={(event) => updateField(
                        'schemaJson',
                        editorStateToAttachmentsSchemaJson({
                          ...attachmentsEditorState,
                          maxFileSizeMb: Math.max(1, Number(event.target.value) || 1),
                        }),
                      )}
                    />
                  </div>
                  <div className={styles.fieldRow}>
                    <label className={styles.label}>{t('maxFiles', { defaultValue: 'Max files' })}</label>
                    <input
                      className="text-input"
                      type="number"
                      min={1}
                      value={attachmentsEditorState.maxFiles}
                      onChange={(event) => updateField(
                        'schemaJson',
                        editorStateToAttachmentsSchemaJson({
                          ...attachmentsEditorState,
                          maxFiles: Math.max(1, Number(event.target.value) || 1),
                        }),
                      )}
                    />
                  </div>
                  <div className={styles.fieldRow}>
                    <label className={styles.label}>{t('allowedAttachmentTypes', { defaultValue: 'Allowed attachment types' })}</label>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {CONSULTATION_ATTACHMENT_TYPE_OPTIONS.map((option) => (
                        <label key={option.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="checkbox"
                            checked={attachmentsEditorState.allowedTypes.includes(option.value)}
                            onChange={(event) => {
                              const nextAllowedTypes = event.target.checked
                                ? [...attachmentsEditorState.allowedTypes, option.value]
                                : attachmentsEditorState.allowedTypes.filter((item) => item !== option.value);
                              updateField(
                                'schemaJson',
                                editorStateToAttachmentsSchemaJson({
                                  ...attachmentsEditorState,
                                  allowedTypes: nextAllowedTypes as ConsultationAttachmentType[],
                                }),
                              );
                            }}
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}

              {usesVoiceSchemaJson(editorState.fieldType) ? (
                <div className={styles.fieldRow}>
                  <label className={styles.label}>{t('voiceSettings', { defaultValue: 'Voice settings' })}</label>
                  <p className={styles.optionsHint}>
                    {t('voiceSettingsHint', {
                      defaultValue: 'Configure recording limits and whether transcription is available for this field.',
                    })}
                  </p>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={voiceEditorState.enableTranscription}
                      onChange={(event) => updateField(
                        'schemaJson',
                        editorStateToVoiceSchemaJson({
                          ...voiceEditorState,
                          enableTranscription: event.target.checked,
                        }),
                      )}
                    />
                    <span>{t('enableTranscription', { defaultValue: 'Enable transcription' })}</span>
                  </label>
                  <div className={styles.fieldRow}>
                    <label className={styles.label}>{t('maxDurationSeconds', { defaultValue: 'Max duration (seconds)' })}</label>
                    <input
                      className="text-input"
                      type="number"
                      min={1}
                      value={voiceEditorState.maxDurationSeconds}
                      onChange={(event) => updateField(
                        'schemaJson',
                        editorStateToVoiceSchemaJson({
                          ...voiceEditorState,
                          maxDurationSeconds: Math.max(1, Number(event.target.value) || 1),
                        }),
                      )}
                    />
                  </div>
                  <div className={styles.fieldRow}>
                    <label className={styles.label}>{t('maxFileSizeMb', { defaultValue: 'Max file size (MB)' })}</label>
                    <input
                      className="text-input"
                      type="number"
                      min={1}
                      value={voiceEditorState.maxFileSizeMb}
                      onChange={(event) => updateField(
                        'schemaJson',
                        editorStateToVoiceSchemaJson({
                          ...voiceEditorState,
                          maxFileSizeMb: Math.max(1, Number(event.target.value) || 1),
                        }),
                      )}
                    />
                  </div>
                </div>
              ) : null}

              <div className={styles.fieldRow}>
                <label className={styles.label}>{t('defaultValue', { defaultValue: 'Default value' })}</label>
                <textarea className="text-input" rows={2} value={editorState.defaultValue} onChange={(event) => updateField('defaultValue', event.target.value)} maxLength={2000} />
              </div>

              {fieldTypeChanged ? (
                <p className={styles.warning}>
                  {t('consultationFieldDefinitionTypeChangeWarning', {
                    defaultValue: 'Changing the field type may make existing values incompatible with the new type.',
                  })}
                </p>
              ) : null}

              {usesOptions(editorState.fieldType) ? (
                <div className={styles.fieldRow}>
                  <label className={styles.label}>{t('options', { defaultValue: 'Options' })}</label>
                  <p className={styles.optionsHint}>
                    {t('consultationFieldDefinitionOptionsHint', {
                      defaultValue: 'Provide one or more options. IDs are optional but must be unique when provided.',
                    })}
                  </p>
                  <p className={styles.optionsHint}>
                    {t('consultationFieldDefinitionOptionsHintAutoId', {
                      defaultValue: 'Leave ID blank to auto-generate from label. Drag rows or use arrows to reorder.',
                    })}
                  </p>
                  {optionValidation.messages.length > 0 ? (
                    <button
                      className={styles.optionsValidationSummary}
                      type="button"
                      onClick={focusFirstInvalidOption}
                    >
                      {optionValidation.messages.join(' ')}
                    </button>
                  ) : null}
                  <div className={styles.optionsList}>
                    {editorState.options.map((option, index) => (
                      <div
                        key={option.key}
                        className={`${styles.optionRow} ${draggedOptionKey === option.key ? styles.optionRowDragging : ''} ${dropTargetOptionKey === option.key ? styles.optionRowDropTarget : ''} ${optionValidation.byRowKey[option.key] ? styles.optionRowInvalid : ''}`}
                        draggable
                        onDragStart={() => handleOptionDragStart(option.key)}
                        onDragEnter={() => setDropTargetOptionKey(option.key)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={() => handleOptionDrop(option.key)}
                        onDragEnd={handleOptionDragEnd}
                      >
                        <span className={styles.optionDragHandle} title={t('dragToReorder', { defaultValue: 'Drag to reorder' })}>⋮⋮</span>
                        <div className={styles.optionFieldColumn}>
                          <input
                            className={`text-input ${optionValidation.byRowKey[option.key]?.id ? styles.optionInputInvalid : ''}`}
                            ref={(element) => {
                              optionInputRefs.current[`${option.key}:id`] = element;
                            }}
                            value={option.id}
                            onChange={(event) => handleUpdateOptionRow(option.key, 'id', event.target.value)}
                            placeholder={t('optionIdPlaceholder', { defaultValue: 'Option ID (optional)' })}
                            maxLength={100}
                          />
                          {optionValidation.byRowKey[option.key]?.id ? (
                            <span className={styles.optionInlineError}>{optionValidation.byRowKey[option.key]?.id}</span>
                          ) : <span className={styles.optionInlineErrorSpacer} />}
                        </div>
                        <div className={styles.optionFieldColumn}>
                          <input
                            className={`text-input ${optionValidation.byRowKey[option.key]?.label ? styles.optionInputInvalid : ''}`}
                            ref={(element) => {
                              optionInputRefs.current[`${option.key}:label`] = element;
                            }}
                            value={option.label}
                            onChange={(event) => handleUpdateOptionRow(option.key, 'label', event.target.value)}
                            placeholder={t('optionLabelPlaceholder', { defaultValue: 'Option label' })}
                            maxLength={200}
                          />
                          {optionValidation.byRowKey[option.key]?.label ? (
                            <span className={styles.optionInlineError}>{optionValidation.byRowKey[option.key]?.label}</span>
                          ) : <span className={styles.optionInlineErrorSpacer} />}
                        </div>
                        <button className="btn btn-secondary" type="button" onClick={() => handleRemoveOptionRow(option.key)}>
                          {t('removeOption', { defaultValue: 'Remove' })}
                        </button>
                        <div className={styles.optionReorderButtons}>
                          <button
                            className="btn btn-secondary"
                            type="button"
                            onClick={() => handleMoveOptionRow(option.key, -1)}
                            disabled={index === 0}
                            title={t('moveOptionUp', { defaultValue: 'Move up' })}
                          >
                            ↑
                          </button>
                          <button
                            className="btn btn-secondary"
                            type="button"
                            onClick={() => handleMoveOptionRow(option.key, 1)}
                            disabled={index === editorState.options.length - 1}
                            title={t('moveOptionDown', { defaultValue: 'Move down' })}
                          >
                            ↓
                          </button>
                        </div>
                        <span className={styles.optionIndex}>#{index + 1}</span>
                      </div>
                    ))}
                  </div>
                  <button className="btn btn-secondary" type="button" onClick={handleAddOptionRow}>
                    {t('addOption', { defaultValue: 'Add option' })}
                  </button>
                </div>
              ) : null}

              <label className={styles.checkboxRow}>
                <input type="checkbox" checked={editorState.isRequired} onChange={(event) => updateField('isRequired', event.target.checked)} />
                <span>{t('fieldRequiredForCompletion', { defaultValue: 'Required when this field is used in consultation workflows' })}</span>
              </label>

              <label className={styles.checkboxRow}>
                <input type="checkbox" checked={editorState.isActive} onChange={(event) => updateField('isActive', event.target.checked)} />
                <span>{t('fieldDefinitionActive', { defaultValue: 'Field definition is active and available for template assignment' })}</span>
              </label>
            </div>

            <div className={styles.footer}>
              {deleteConfirmPending ? (
                <>
                  <span>{t('consultationFieldDefinitionDeleteConfirm', { defaultValue: 'Delete "{{name}}"? This cannot be undone.', name: selectedDefinition?.name ?? '' })}</span>
                  <button className="btn btn-secondary" type="button" onClick={handleDeleteCancel} disabled={saving}>
                    {t('cancel', { ns: 'common', defaultValue: 'Cancel' })}
                  </button>
                  <button className="btn btn-danger" type="button" onClick={() => void handleDeleteConfirm()} disabled={saving}>
                    {saving ? t('deleting', { ns: 'common', defaultValue: 'Deleting…' }) : t('confirmDelete', { defaultValue: 'Yes, delete' })}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => void handleApprove()}
                    disabled={!selectedDefinition || !selectedDefinition.createdByAi || selectedDefinition.aiStatus !== 'pendingApproval' || saving}
                  >
                    {t('approve', { defaultValue: 'Approve' })}
                  </button>
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => void handleReject()}
                    disabled={!selectedDefinition || !selectedDefinition.createdByAi || selectedDefinition.aiStatus !== 'pendingApproval' || saving}
                  >
                    {t('reject', { defaultValue: 'Reject' })}
                  </button>
                  <button className="btn btn-secondary" type="button" onClick={handleCreateNew} disabled={saving}>
                    {t('resetForm', { defaultValue: 'Reset form' })}
                  </button>
                  <button
                    className="btn btn-danger"
                    type="button"
                    onClick={handleDeleteRequest}
                    disabled={!selectedDefinition || saving}
                  >
                    {t('deleteFieldDefinition', { defaultValue: 'Delete field definition' })}
                  </button>
                  <button className="btn btn-primary" type="button" onClick={() => void handleSave()} disabled={saving}>
                    {saving
                      ? t('saving', { ns: 'common', defaultValue: 'Saving…' })
                      : selectedDefinition
                        ? t('saveChanges', { defaultValue: 'Save changes' })
                        : t('createFieldDefinition', { defaultValue: 'Create field definition' })}
                  </button>
                </>
              )}
            </div>
          </section>
        </section>
      )}
    </div>
  );
};

export default ConsultationFieldDefinitionsPage;